import { useEffect } from 'react';
import { motion } from 'framer-motion';
import { X, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useVoiceRecorder } from '@/hooks/useVoiceRecorder';

interface VoiceRecorderProps {
  onSend: (blob: Blob, duration: number) => void;
  onCancel: () => void;
}

export function VoiceRecorder({ onSend, onCancel }: VoiceRecorderProps) {
  const {
    isRecording,
    duration,
    audioBlob,
    analyserData,
    startRecording,
    stopRecording,
    cancelRecording,
  } = useVoiceRecorder();

  useEffect(() => { startRecording(); }, []);

  useEffect(() => {
    if (audioBlob && duration > 0) {
      onSend(audioBlob, duration);
    }
  }, [audioBlob]);

  const formatDuration = (s: number) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  const bars = analyserData
    ? Array.from(analyserData).slice(0, 28).map((v) => Math.max(3, (v / 255) * 28))
    : Array.from({ length: 28 }, () => 3);

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 8 }}
      className="flex items-center gap-2 px-3 py-3 border-t border-border/40 bg-background safe-bottom"
    >
      {/* Cancel */}
      <Button variant="ghost" size="icon" className="h-9 w-9 rounded-full shrink-0" onClick={() => { cancelRecording(); onCancel(); }}>
        <X className="h-5 w-5 text-destructive" />
      </Button>

      {/* Recording indicator + Waveform */}
      <div className="flex-1 flex items-center gap-2.5">
        <motion.div
          animate={{ scale: [1, 1.3, 1], opacity: [1, 0.4, 1] }}
          transition={{ repeat: Infinity, duration: 1.4, ease: 'easeInOut' }}
          className="h-2.5 w-2.5 rounded-full bg-destructive shrink-0"
        />

        <div className="flex items-center gap-[2px] h-7 flex-1">
          {bars.map((h, i) => (
            <motion.div
              key={i}
              className="w-[3px] bg-primary/60 rounded-full"
              animate={{ height: `${h}px` }}
              transition={{ duration: 0.08 }}
            />
          ))}
        </div>

        <span className="text-[13px] font-medium tabular-nums text-muted-foreground shrink-0">
          {formatDuration(duration)}
        </span>
      </div>

      {/* Send */}
      <motion.div
        animate={{ scale: duration >= 1 ? 1 : 0.8, opacity: duration >= 1 ? 1 : 0.4 }}
      >
        <Button
          size="icon"
          className="h-9 w-9 rounded-full shrink-0"
          onClick={stopRecording}
          disabled={duration < 1}
        >
          <Send className="h-4 w-4" />
        </Button>
      </motion.div>
    </motion.div>
  );
}
